import React, { Component } from "react";
import {
  Modal,
  FormInput,
  FormSelect,
  Button,
  Dropzone,
  Loader,
} from "ui-kit-ck-consultant";

import axiosConfig from "../../../utils/axiosConfig";
import {
  getUsers,
  getAgencies,
  getGroups,
  getRegions,
} from "../../../utils/general";

import AuthContext from "../../../context/AuthContext";

// Sentinel for the "Tous les groupes / Toutes les régions" pseudo-options.
// It must be truthy: FormSelect renders `value ? value : "DEFAULT"`, so a falsy
// value (e.g. "" or 0) would be coerced back to the "DEFAULT" placeholder and
// the option could never stay selected. It is converted back to "" in getData()
// so the backend stores it as NULL (= applies to every group/region).
const ALL_VALUE = "all";

// Sentinel for the explicit "Aucune compagnie" option. Like ALL_VALUE it must be
// truthy (FormSelect renders `value ? value : "DEFAULT"`). It is converted back to
// "" in getData() so the backend stores cm_fk_company_id as NULL (= no mandant).
const NO_COMPANY = "none";

export default class ModalCompanyMonitoring extends Component {
  constructor(props) {
    super(props);
    this.state = {
      isLoad: false,
      companyOptions: [],
      groupOptions: [],
      regionOptions: [],
      agencyOptions: [],
      documentOptions: [],
      editorOptions: [],
      date: "",
      company: "DEFAULT",
      group: "DEFAULT",
      region: "DEFAULT",
      agency: "DEFAULT",
      document: "DEFAULT",
      editor: "DEFAULT",
      director: "",
      location: "",
      name: "",
      dataBlob: null,
      dataOriginName: null,
      dataMime: null,
      // Other
      alerts: [],
    };
    this.initialState = this.state;
  }

  static contextType = AuthContext;

  componentDidMount() {
    this.getGroups();
    this.getEditors();
    this.setState({
      editor: this.context.name,
    });
  }

  handleInputChange = (e) => {
    const { name, value } = e.target;
    // Reset the child selects when a parent (group → region → agency) changes,
    // otherwise a previously chosen region/agency would linger in state even
    // though it no longer belongs to the new parent and its select is disabled.
    const reset = {};
    if (name === "group") {
      reset.region = "DEFAULT";
      reset.agency = "DEFAULT";
    } else if (name === "region") {
      reset.agency = "DEFAULT";
    }
    this.setState(
      {
        [name]: value,
        ...reset,
      },
      () => {
        if (name === "group") {
          this.getRegions();
          this.getEditors();
        } else if (name === "region") {
          this.getAgencies();
          this.getEditors();
        }
      }
    );
  };

  // Collect the labels of every required field that is missing/invalid, so the
  // user gets a precise message ("Veuillez renseigner : Nom, Date, …") instead
  // of a generic "Une erreur est survenue". Company is intentionally optional
  // (support "sans mandant"), so it is never validated here.
  getValidationErrors() {
    const errors = [];

    if (!this.state.name) errors.push("Nom");
    if (!this.state.date) errors.push("Date");

    const hasScope =
      this.state.group !== "DEFAULT" ||
      this.state.region !== "DEFAULT" ||
      this.state.agency !== "DEFAULT";
    if (!hasScope) errors.push("Groupe / Région / Agence");

    // Type-specific fields are only required when creating (not when editing).
    if (!this.props.isEdit) {
      if (this.props.type === 1) {
        if (
          !this.state.dataBlob ||
          !this.state.dataOriginName ||
          !this.state.dataMime
        ) {
          errors.push("Fichier");
        }
      } else {
        if (!this.state.document || this.state.document === "DEFAULT") {
          errors.push("Modèle");
        }
        if (!this.state.editor || this.state.editor === "DEFAULT") {
          errors.push("Éditeur");
        }
        if (!this.state.director) errors.push("Dirigé par");
        if (!this.state.location) errors.push("Lieu");
      }
    }

    return errors;
  }

  handleValidClick = () => {
    const errors = this.getValidationErrors();
    if (errors.length > 0) {
      alert(
        "Veuillez renseigner le(s) champ(s) suivant(s) :\n- " +
          errors.join("\n- ")
      );
      return;
    }

    this.setState({ isLoad: true }, () => {
      if (!this.props.isEdit) {
        axiosConfig("company-monitoring/insert", {
          data: this.getData(),
        }).then((res) => {
          if (res.data.success) {
            this.setState(this.initialState);
            this.props.onClose();
          } else {
            this.setState({ isLoad: false }, () =>
              alert("Une erreur est survenue lors de l'enregistrement")
            );
          }
        });
      } else {
        let data = this.getData();
        data["id"] = this.props.id;
        axiosConfig("company-monitoring/update", {
          data: data,
        }).then((res) => {
          if (res.data.success) {
            this.setState({
              isLoad: false,
              name: "",
              date: "",
              company: "DEFAULT",
              group: "DEFAULT",
              region: "DEFAULT",
              agency: "DEFAULT",
            });
            this.props.onClose();
          } else {
            this.setState({ isLoad: false }, () =>
              alert("Une erreur est survenue lors de l'enregistrement")
            );
          }
        });
      }
    });
  };

  getData() {
    // Map the "all" sentinel back to "" so the backend stores it as NULL.
    const normalize = (value) => (value === ALL_VALUE ? "" : value);
    return {
      name: this.state.name,
      date: this.state.date,
      // "Aucune compagnie" → "" so the backend stores cm_fk_company_id as NULL.
      company: this.state.company === NO_COMPANY ? "" : this.state.company,
      group: normalize(this.state.group),
      region: normalize(this.state.region),
      agency: normalize(this.state.agency),
      document: this.state.document,
      editor: this.state.editor,
      director: this.state.director,
      location: this.state.location,
      base64: this.state.dataBlob,
      mime: this.state.dataMime,
      originName: this.state.dataOriginName,
      type: this.props.type,
    };
  }

  getGroups = () => {
    getGroups((groupOptions) => {
      if (
        this.context.role > 2 ||
        !this.context.group ||
        (this.state.group && this.state.group !== "DEFAULT")
      ) {
        this.setState({ groupOptions }, () => this.getRegions());
      } else {
        const groupList = [];
        groupList.push(
          groupOptions.find((element) => element.value === this.context.group)
        );
        this.setState(
          {
            groupOptions: groupList ? groupList : null,
          },
          () => this.getRegions()
        );
      }
    }, false);
  };

  getRegions = () => {
    getRegions(
      (regionOptions) => {
        this.setState(
          { regionOptions, region: this.state.region || "DEFAULT" },
          () => this.getAgencies()
        );
      },
      false,
      this.state.group !== "DEFAULT" ? this.state.group : this.context.group
    );
  };

  getAgencies = () => {
    getAgencies(
      (agencyOptions) => {
        this.setState({
          agencyOptions,
          agency: this.state.agency || "DEFAULT",
        });
      },
      true,
      this.state.region !== "DEFAULT" ? this.state.region : this.context.region
    );
  };

  getEditors() {
    getUsers(
      this.context.network,
      this.context.group,
      this.context.region,
      null,
      null,
      null,
      (editorOptions) => this.setState({ editorOptions })
    );
  }

  getDocuments() {
    axiosConfig("company-monitoring/get-documents", {
      data: {
        type: this.props.type,
      },
    }).then((res) => {
      if (res.data.success === true) {
        this.setState({
          document: "DEFAULT",
          documentOptions: res.data.data,
        });
      } else {
        this.setState({
          document: "DEFAULT",
          documentOptions: [],
        });
      }
    });
  }

  componentDidUpdate(prevProps) {
    if (
      this.props.show === true &&
      this.props.isEdit === true &&
      this.props.isEdit !== prevProps.isEdit
    ) {
      axiosConfig("company-monitoring/get", {
        data: { id: this.props.id },
      }).then((res) => {
        if (res.data.success === true) {
          // Preload the existing scope (group → region → agency) and company so
          // the cascading selects show the current values; getGroups() then
          // cascades to getRegions()/getAgencies(), which preserve these values
          // (`region: this.state.region || "DEFAULT"`, idem agency). Without this
          // an update would wipe the scope to NULL — hence the old red warning.
          this.setState(
            {
              date: res.data.data.date,
              company: res.data.data.company
                ? res.data.data.company
                : NO_COMPANY,
              group: res.data.data.group ? res.data.data.group : "DEFAULT",
              region: res.data.data.region ? res.data.data.region : "DEFAULT",
              agency: res.data.data.agency ? res.data.data.agency : "DEFAULT",
              name: res.data.data.name,
            },
            () => {
              this.getGroups();
              this.getEditors();
            }
          );
        }
      });
    }

    if (
      this.props.show === true &&
      this.props.isEdit === false &&
      this.props.show !== prevProps.show
    ) {
      this.getDocuments();
      this.getGroups();
      this.getRegions();
      this.getAgencies();
    }
  }

  blobValue = (e) => {
    this.setState({
      dataBlob: e.base64,
      dataMime: e.mime,
      dataOriginName: e.name,
    });
  };

  render() {
    let companyOptions = [...this.context.companyOptions];
    companyOptions.splice(0, 1);

    return (
      <>
        <Modal
          show={this.props.show}
          onClose={() => {
            this.setState(this.initialState);
            this.props.onClose();
          }}
          title={
            !this.props.isEdit
              ? `Ajouter ${this.props.name}`
              : `Modifier ${this.props.name}`
          }
        >
          <FormInput
            className={"my-10"}
            type="date"
            placeholder="Date"
            title="Date"
            name="date"
            value={this.state.date}
            onChange={this.handleInputChange}
          />
          <FormSelect
            className={"my-10"}
            title="Compagnie"
            init="Veuillez sélectionner une compagnie"
            name="company"
            ignore={true}
            value={this.state.company}
            options={[
              { value: NO_COMPANY, text: "Aucune compagnie" },
              ...companyOptions,
            ]}
            onChange={this.handleInputChange}
          />
          <FormSelect
            className={"my-10"}
            title="Groupe"
            init="Veuillez sélectionner un groupe"
            name="group"
            ignore={true}
            value={this.state.group}
            options={[
              { value: ALL_VALUE, text: "Tous les groupes" },
              ...this.state.groupOptions,
            ]}
            onChange={this.handleInputChange}
          />
          <FormSelect
            className={"my-10"}
            title="Région"
            init="Veuillez sélectionner une région"
            name="region"
            ignore={true}
            value={this.state.region}
            options={[
              { value: ALL_VALUE, text: "Toutes les régions" },
              ...this.state.regionOptions,
            ]}
            onChange={this.handleInputChange}
            disabled={this.state.group === "DEFAULT"}
          />
          <FormSelect
            className={"my-10"}
            title="Agence"
            init="Veuillez sélectionner une agence"
            name="agency"
            ignore={true}
            value={this.state.agency}
            options={[...this.state.agencyOptions]}
            onChange={this.handleInputChange}
            disabled={
              this.state.region === "DEFAULT" ||
              this.state.region === ALL_VALUE
            }
          />
          {this.props.isEdit === false && this.props.type !== 1 ? (
            <FormSelect
              className={"my-10"}
              title="Modèle"
              init="Veuillez sélectionner un modèle"
              name="document"
              ignore={true}
              value={this.state.document}
              options={this.state.documentOptions}
              onChange={this.handleInputChange}
            />
          ) : null}
          {this.props.isEdit === false && this.props.type !== 1 ? (
            <FormSelect
              className={"my-10"}
              title="Éditeur"
              init="Veuillez sélectionner un éditeur"
              name="editor"
              ignore={true}
              value={this.state.editor}
              options={this.state.editorOptions}
              onChange={this.handleInputChange}
            />
          ) : null}
          {this.props.isEdit === false && this.props.type !== 1 ? (
            <FormInput
              className={"my-10"}
              type="text"
              placeholder="Dirigé par"
              title="Dirigé par"
              name="director"
              value={this.state.director}
              onChange={this.handleInputChange}
            />
          ) : null}
          {this.props.isEdit === false && this.props.type !== 1 ? (
            <FormInput
              className={"my-10"}
              type="text"
              placeholder="Lieu"
              title="Lieu"
              name="location"
              value={this.state.location}
              onChange={this.handleInputChange}
            />
          ) : null}
          <FormInput
            className={"my-10"}
            type="text"
            placeholder="Nom"
            title="Nom"
            name="name"
            value={this.state.name}
            onChange={this.handleInputChange}
          />
          {this.props.isEdit === false && this.props.type === 1 ? (
            <Dropzone
              className={"m-auto my-10"}
              dropHandler={this.blobValue}
              typeName={[
                "docx",
                "doc",
                "xls",
                "xlsx",
                "cdv",
                "ppt",
                "pdf",
                "odt",
                "jpeg",
                "png",
                "mp4",
              ]}
            />
          ) : null}
          <Button
            className={"my-10"}
            text="Valider"
            onClick={() => this.handleValidClick()}
          />
        </Modal>
        <Loader show={this.state.isLoad} />
      </>
    );
  }
}
